import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { bookingApi } from '../../../utils/bookingApi';
import { useAuth } from '../../../contexts/AuthContext';
import { formatDayDate } from '../../../utils/dates';
import { PageHeader, Skeleton, ErrorState, useConfirm, useToast } from '../../../components/ui';
import { SignaturesPanel, SharePanel } from '../../../components/welfare/WelfareSignatures';
import '../../../components/welfare/WelfareSignatures.css';
import AttachmentTile from '../../../components/welfare/AttachmentTile';
import { CONCERN_TYPE_LABEL } from '../../../components/welfare/welfareLabels';
import { WELFARE_PATH, SeverityPill, subjectName } from './AdminWelfare';
import '../booking-shared.css';
import './Safeguarding.css';

function Section({ title, children }) {
  return (
    <section className="sg-section">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function AttachmentsPanel({ report, onChange }) {
  const confirm = useConfirm();
  const toast = useToast();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const attachments = report.attachments || [];

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      const res = await bookingApi.uploadWelfareAttachments(report.id, files);
      onChange({ ...report, attachments: [...attachments, ...res.data] });
      toast(res.data.length === 1 ? 'File added' : `${res.data.length} files added`);
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't upload. Try again.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDelete = async (attachment) => {
    const ok = await confirm({
      title: 'Delete this file?',
      message: `${attachment.fileName} is removed from the report for good.`,
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    setError(null);
    try {
      await bookingApi.deleteWelfareAttachment(report.id, attachment.id);
      onChange({ ...report, attachments: attachments.filter(a => a.id !== attachment.id) });
      toast('File deleted');
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't delete the file. Try again.");
    }
  };

  return (
    <Section title={`Files${attachments.length > 0 ? ` (${attachments.length})` : ''}`}>
      {attachments.length > 0 && (
        <div className="sg-files">
          {attachments.map(a => (
            <AttachmentTile key={a.id} attachment={a} load={() => bookingApi.fetchWelfareAttachment(report.id, a.id)} onDelete={() => handleDelete(a)} />
          ))}
        </div>
      )}
      <label className="bk-label">
        Add photos, videos or PDFs
        <span className="sg-hint">Short video clips are fine. Up to 25 MB each, 6 files at a time.</span>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/heic,image/heif,video/mp4,video/quicktime,application/pdf"
          multiple
          capture="environment"
          onChange={e => handleFiles(e.target.files)}
          disabled={uploading}
        />
      </label>
      {uploading && <p className="bk-small" role="status">Uploading…</p>}
      {error && <p className="bk-form-error" role="alert">{error}</p>}
    </Section>
  );
}

// One welfare report on its own page
export default function AdminWelfareDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();
  const { user } = useAuth();
  const [current, setCurrent] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    setLoadError(false);
    return bookingApi.getWelfareReport(id)
      .then(res => setCurrent(res.data))
      .catch((err) => { console.error('Could not load the welfare report', err); setLoadError(true); });
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // Files are part of what's signed, so reload the report after they change
  const reload = () => bookingApi.getWelfareReport(id).then(res => setCurrent(res.data))
    .catch((err) => { console.error('Could not reload the welfare report', err); setError("Couldn't refresh the report. Reload the page."); });

  const handleDelete = async () => {
    const ok = await confirm({
      title: 'Delete this welfare report?',
      message: "It's removed for good, with its files, signatures and shares.",
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    setDeleting(true);
    setError(null);
    try {
      await bookingApi.deleteWelfareReport(id);
      toast('Welfare report deleted');
      navigate(WELFARE_PATH, { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't delete the report. Try again.");
      setDeleting(false);
    }
  };

  const back = { to: WELFARE_PATH, label: 'Welfare reports' };

  if (loadError || !current) {
    return (
      <div className="bk-page bk-page--narrow">
        <PageHeader back={back} title="Welfare report" />
        {loadError
          ? <ErrorState title="Couldn't load the report" onRetry={load} />
          : <Skeleton rows={4} label="Loading the report…" />}
      </div>
    );
  }

  return (
    <div className="bk-page bk-page--narrow">
      <PageHeader
        back={back}
        title="Welfare report"
        details={`${subjectName(current)} · ${formatDayDate(current.incidentDate)}`}
        action={{ label: 'Edit', to: `${WELFARE_PATH}/${id}/edit` }}
      />

      {error && <p className="bk-form-error" role="alert">{error}</p>}

      <div className="bk-card">
        <dl className="sg-facts">
          <div><dt>Type</dt><dd>{CONCERN_TYPE_LABEL[current.concernType] ?? current.concernType}</dd></div>
          <div><dt>Severity</dt><dd><SeverityPill severity={current.severity} /></dd></div>
          {current.location && <div><dt>Location</dt><dd>{current.location}</dd></div>}
          <div><dt>Filed by</dt><dd>{current.reportedBy.firstName} {current.reportedBy.lastName}</dd></div>
        </dl>

        <Section title="Description"><p>{current.description}</p></Section>
        {current.actionTaken && <Section title="Action taken"><p>{current.actionTaken}</p></Section>}
        {current.outcome && <Section title="Outcome"><p>{current.outcome}</p></Section>}
        {(current.witnessName || current.witnessContact || current.witnessStatement) && (
          <Section title="Witness account">
            {current.witnessName && <p className="sg-line"><strong>Name:</strong> {current.witnessName}</p>}
            {current.witnessContact && <p className="sg-line"><strong>Contact:</strong> {current.witnessContact}</p>}
            {current.witnessStatement && <p>{current.witnessStatement}</p>}
          </Section>
        )}
        {(current.referredExternally || current.referralDetails) && (
          <Section title="External referral">
            <p>{current.referredExternally ? 'Referred to an outside agency.' : 'Not referred to an outside agency.'}</p>
            {current.referralDetails && <p>{current.referralDetails}</p>}
          </Section>
        )}
      </div>

      <div className="bk-card">
        <AttachmentsPanel report={current} onChange={(updated) => { setCurrent(updated); reload(); }} />
      </div>
      <div className="bk-card">
        <Section title="Signatures">
          <SignaturesPanel report={current} currentUserId={user?.id} onChange={setCurrent} />
        </Section>
      </div>
      <div className="bk-card">
        <Section title="Shared with">
          <SharePanel report={current} onChange={setCurrent} />
        </Section>
      </div>

      <div className="bk-actions">
        <button type="button" className="bk-btn bk-btn--outline-danger" onClick={handleDelete} disabled={deleting}>
          {deleting ? 'Deleting…' : 'Delete report'}
        </button>
      </div>
    </div>
  );
}
